import React from "react";

import { KeyboardArrowDown } from "@mui/icons-material";
import { Autocomplete, TextField } from "@mui/material";

import useAuth from "@app/common/auth/useAuth";
import { EnterpriseWorkspaceUser } from "@app/common/workspaces/types";
import useWorkspace from "@app/common/workspaces/useWorkspace";

import WorkspaceMemberAutocompleteOption from "./WorkspaceMemberAutocompleteOption";

const getOptionLabel = (option: EnterpriseWorkspaceUser) =>
  option.fullName ?? option.email;

const matchesEmail = (
  userEmail: EnterpriseWorkspaceUser["email"],
  searchTerm: string
) => {
  if (searchTerm.includes("@")) {
    return userEmail.includes(searchTerm);
  }

  const userEmailWithoutDomain = userEmail.replace(/@.+/, "");
  return userEmailWithoutDomain.includes(searchTerm);
};

const filterUsers = (
  users: EnterpriseWorkspaceUser[],
  state: {
    inputValue: string;
    getOptionLabel: (user: EnterpriseWorkspaceUser) => string;
  }
) => {
  const caseInsensitiveValue = state.inputValue.toLowerCase().trim();
  return users.filter(
    (user) =>
      matchesEmail(user.email.toLowerCase(), caseInsensitiveValue) ||
      state.getOptionLabel(user).toLowerCase().includes(caseInsensitiveValue)
  );
};

const WorkspaceMemberAutocomplete = ({
  disabled,
  selected,
  onSelect,
}: {
  disabled: boolean;
  selected: EnterpriseWorkspaceUser | null;
  onSelect: (user: EnterpriseWorkspaceUser | null) => void;
}) => {
  const { user } = useAuth();
  const { workspace } = useWorkspace();

  const handleTextFieldKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const handleAutocompleteChange = (
    event: React.SyntheticEvent,
    value: EnterpriseWorkspaceUser | null
  ) => {
    onSelect(value);
  };

  const selectableUsers = workspace.users.filter((u) => u.userId !== user?.id);

  return (
    <Autocomplete
      autoComplete
      clearOnEscape
      clearOnBlur={false}
      color="primary"
      disabled={disabled}
      popupIcon={<KeyboardArrowDown />}
      filterOptions={filterUsers}
      options={selectableUsers}
      isOptionEqualToValue={(option, value) => option.email === value.email}
      getOptionLabel={getOptionLabel}
      onChange={handleAutocompleteChange}
      renderInput={(props) => (
        <TextField
          {...props}
          placeholder={
            selected == null ? "Select Workspace user..." : undefined
          }
          onKeyDown={handleTextFieldKeyDown}
          disabled={disabled}
        />
      )}
      renderOption={(props, user) => (
        <WorkspaceMemberAutocompleteOption {...props} user={user} />
      )}
      sx={{
        "& .MuiAutocomplete-inputRoot": {
          py: 0.5,
          input: {
            fontSize: "1rem",
          },
        },
      }}
    />
  );
};

export default WorkspaceMemberAutocomplete;
